import { Button, TextArea } from '@scale/scaleui';
import { FeedbackResponseType, WorkerCommentEntry } from '@scale/shared/types/chat/feedback';
import clsx from 'clsx';
import { isNil } from 'lodash';
import React, { useCallback, useRef, useState } from 'react';
import { useWorkerCommentsStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useWorkerCommentsStore';
import { StepCard } from 'react-scale/components/scalers/tasks/ThreadedInteraction/step/StepCard';
import { twMerge } from 'tailwind-merge';

interface ViewEditCommentBoxProps {
  workerComment: WorkerCommentEntry;
  selectedCommentIndex: number; // the index of the currently selected worker comment
  unresolvedComments: WorkerCommentEntry[]; // all unresolved comments for this step
  canEdit: boolean;
  readOnly: boolean;
  isInlinePosition?: boolean;
}

const ViewEditCommentBox: React.FC<ViewEditCommentBoxProps> = ({
  workerComment,
  selectedCommentIndex,
  unresolvedComments,
  canEdit,
  readOnly,
  isInlinePosition = false,
}) => {
  const updateWorkerComment = useWorkerCommentsStore(state => state.updateWorkerComment);
  const deleteWorkerComment = useWorkerCommentsStore(state => state.deleteWorkerComment);
  const addFeedbackResponseToWorkerComment = useWorkerCommentsStore(
    state => state.addFeedbackResponseToWorkerComment,
  );
  const selectWorkerComment = useWorkerCommentsStore(state => state.selectWorkerComment);

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editedCommentText, setEditedCommentText] = useState<string>(workerComment.comment);

  const selectPrevious =
    !isEditing && selectedCommentIndex - 1 >= 0 && unresolvedComments.at(selectedCommentIndex - 1)
      ? () => {
          selectWorkerComment(unresolvedComments.at(selectedCommentIndex - 1)!.workerCommentId);
        }
      : undefined;
  const selectNext =
    !isEditing &&
    selectedCommentIndex + 1 < unresolvedComments.length &&
    unresolvedComments.at(selectedCommentIndex + 1)
      ? () => {
          selectWorkerComment(unresolvedComments.at(selectedCommentIndex + 1)!.workerCommentId);
        }
      : undefined;

  const onEdit = () => {
    setEditedCommentText(workerComment.comment);
    setIsEditing(true);
  };

  const getNextComment = () => {
    if (!isNil(selectPrevious)) {
      selectPrevious();
    } else if (!isNil(selectNext)) {
      selectNext();
    } else {
      selectWorkerComment(null);
    }
  };

  const onDelete = () => {
    deleteWorkerComment(workerComment.workerCommentId);
    getNextComment();
  };

  const submitWorkerCommentEdits = useCallback(() => {
    if (editedCommentText.length) {
      updateWorkerComment(workerComment.workerCommentId, editedCommentText);
    }
    setIsEditing(false);
  }, [editedCommentText, updateWorkerComment, workerComment]);

  const discardWorkerCommentEdits = useCallback(() => {
    setEditedCommentText(workerComment.comment);
    setIsEditing(false);
  }, [setEditedCommentText, workerComment]);

  const onKeyDownSubmit = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.shiftKey && e.key === 'Enter') {
        e.preventDefault();
        submitWorkerCommentEdits();
      }
      if (e.key === 'Escape') {
        discardWorkerCommentEdits();
      }
    },
    [submitWorkerCommentEdits, discardWorkerCommentEdits],
  );

  const rateComment = (feedbackResponse: FeedbackResponseType) => {
    addFeedbackResponseToWorkerComment(workerComment.workerCommentId, feedbackResponse);
    getNextComment();
  };

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const handleTextAreaInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditedCommentText(e.target.value);
  };

  return (
    <StepCard
      className={clsx('text-neutral-700 text-left text-sm break-words', {
        'max-w-xs': !isEditing && !isInlinePosition,
        'w-80': isEditing || isInlinePosition,
      })}
    >
      <StepCard.Header className="px-1 flex flex-col border-b border-neutral-200 mb-2">
        <div className="w-full flex flex-row justify-start">
          <StepCard.HeaderLeft className="gap-1 flex flex-row justify-start">
            <StepCard.Button
              icon="chevron-left"
              onClick={selectPrevious}
              disabled={isNil(selectPrevious)}
            />
            <StepCard.Button
              icon="chevron-right"
              onClick={selectNext}
              disabled={isNil(selectNext)}
            />
          </StepCard.HeaderLeft>
          <StepCard.HeaderRight>
            {!canEdit && !readOnly && (
              <>
                <StepCard.Button
                  onClick={() => {
                    rateComment(FeedbackResponseType.Accept);
                  }}
                  icon={[
                    workerComment.feedbackResponse === FeedbackResponseType.Accept ? 'fas' : 'far',
                    'thumbs-up',
                  ]}
                />

                <StepCard.Button
                  onClick={() => {
                    rateComment(FeedbackResponseType.Dismiss);
                  }}
                  icon={[
                    workerComment.feedbackResponse === FeedbackResponseType.Dismiss ? 'fas' : 'far',

                    'thumbs-down',
                  ]}
                />
              </>
            )}
            {canEdit && !readOnly && (
              <>
                <StepCard.Button onClick={onEdit} icon={['far', 'edit']} disabled={isEditing} />
                <StepCard.Button onClick={onDelete} icon={['far', 'trash-alt']} />
              </>
            )}
          </StepCard.HeaderRight>
        </div>
        {!isEditing && !!workerComment.title && (
          <StepCard.Subtitle className="px-2 w-full font-semibold">
            {workerComment.title}
          </StepCard.Subtitle>
        )}
      </StepCard.Header>
      <div
        className={twMerge(
          'px-3 pb-2 w-full',
          !isEditing && 'max-h-64 overflow-y-auto whitespace-pre-wrap',
        )}
      >
        {!isEditing ? (
          workerComment.comment
        ) : (
          <TextArea
            className="pt-1"
            ref={textareaRef}
            id="dynamic-textarea"
            rows={1}
            onFocus={(e: React.FocusEvent<HTMLTextAreaElement>) => {
              const textArea = e.target;
              textArea.style.height = 'auto';
              textArea.style.height = textArea.scrollHeight + 'px';
              textArea.style.maxHeight = '16rem';
            }}
            autoFocus
            dynamic
            resizable={false}
            onChange={handleTextAreaInputChange}
            value={editedCommentText}
            onKeyDown={onKeyDownSubmit}
          />
        )}
      </div>
      {isEditing && (
        <StepCard.Footer className="flex gap-2 justify-end border-t-0">
          <Button onClick={discardWorkerCommentEdits} size="sm" variant="neutral">
            Cancel
          </Button>
          <Button onClick={submitWorkerCommentEdits} size="sm" variant="primary">
            Save
          </Button>
        </StepCard.Footer>
      )}
    </StepCard>
  );
};

export default ViewEditCommentBox;
